import { extractIdFromShopifyStr } from '@/utils/helpers';
import {
  AutomaticDiscountPreviewResponse,
  BfcmData,
} from '@/types/bfcmDiscount';
import {
  DisplayPrice,
  createAttributesMap,
  createGetPropertyFunction,
  parseRubyHashToJson,
} from '@/lib/cart/helpers';
import { getSubscriberStatuses } from '@/lib/api/subscriptionManager/apis/getSubscriberStatuses';
import { CartLineNode } from '../shopify';
import { ITEM_ORIGINS } from '@/core/constants';

/**
 * Server-side function to determine if BFCM feature flag is enabled for a user.
 * Matches the logic from useFeatureBfcm hook but uses the modern getSubscriberStatuses API.
 *
 * Rules:
 * - 'off': Always returns false
 * - 'all': Always returns true
 * - 'presale': Returns true only if user is authenticated and has active or aged_out subscriber status
 *   - Defaults to false unless we can confirm eligibility (fail-safe)
 *
 * @param flagValue - The BFCM feature flag value
 * @param authUser - Authenticated user with userUuid and authToken, or null if not authenticated
 * @returns Promise<boolean> - true if BFCM feature flag is enabled, false otherwise
 */
export async function getBfcmEnabledState(
  flagValue: 'off' | 'all' | 'presale',
  authUser: { userUuid: string; authToken: string } | null,
): Promise<boolean> {
  if (flagValue === 'off') {
    return false;
  }

  if (flagValue === 'all') {
    return true;
  }

  if (flagValue === 'presale') {
    // For presale, user must be authenticated
    if (!authUser?.userUuid) {
      return false;
    }

    try {
      // Check subscriber status - defaults to false unless we can confirm eligibility
      const subscriberStatusesResponse = await getSubscriberStatuses({
        userUuid: authUser.userUuid,
        authToken: authUser.authToken,
      });

      // Only enable if we successfully get the response and user is active or aged_out
      if (
        subscriberStatusesResponse.ok &&
        'data' in subscriberStatusesResponse
      ) {
        const { aged_out, active } = subscriberStatusesResponse.data;
        // Enable if user is active or aged_out subscriber (matches useFeatureBfcm logic)
        return aged_out || active;
      }
    } catch (error) {
      console.error('getSubscriberStatuses failed:', error);
      // Fall through to return false
    }

    // Default to false if request fails or response is invalid (fail-safe)
    return false;
  }

  // Default to false for any other flag value
  return false;
}

/**
 * Result of applying BFCM discount to an item
 */
export interface BfcmDiscountResult {
  price?: DisplayPrice;
  hasDiscount: boolean;
}

/**
 * Determines if BFCM mode should be enabled for the user
 *
 * BFCM is enabled if:
 * 1. Feature flag is ON
 * 2. AND one of the following:
 *    - At least one product is already qualified (has qualified === true)
 *    - OR (when no qualified products exist):
 *      * For discount value === 10: Enable BFCM if cart total < $80
 *        (User is on path to qualification and just needs to reach $80 threshold)
 *      * For discount value > 10: Disable BFCM
 *        (Products are not eligible at all, regardless of cart total)
 *
 * Context: When a user has no qualified products, it could mean:
 * - They haven't reached the $80 minimum spend threshold yet (enable BFCM to show progress)
 * - They've already spent $80+ but products are not eligible (disable BFCM)
 *
 * @param featureFlagEnabled - BFCM feature flag status
 * @param serverAutomaticDiscount - Server automatic discount response
 * @returns true if BFCM should be enabled, false otherwise
 */
export function shouldEnableBfcm(
  featureFlagEnabled: boolean,
  minimumThreshold: number | null,
  serverAutomaticDiscount?: AutomaticDiscountPreviewResponse | null,
): boolean {
  // Feature flag must be enabled
  if (!featureFlagEnabled || !minimumThreshold) {
    return false;
  }

  // Check if there's at least one qualified product
  const hasQualifiedProduct =
    serverAutomaticDiscount?.automatic_discount_preview?.items?.some(
      (item) => item.qualified === true,
    ) ?? false;

  // If there are qualified products, enable BFCM
  // (Products have already reached the $80 threshold and are eligible)
  if (hasQualifiedProduct) {
    return true;
  }

  // If no qualified products, determine if user is on path to qualification
  // or if products are not eligible at all
  const discountValue =
    serverAutomaticDiscount?.automatic_discount_preview?.discount
      ?.applicable_value;

  // If no discount value available, disable BFCM
  if (discountValue === undefined || discountValue === null) {
    return false;
  }

  // If discount value > 10, products are not eligible regardless of cart total
  // (User has reached $80+ but products are still not qualified = not eligible)
  if (discountValue > 10) {
    return false;
  }

  // If discount value === 10, check if user has reached the $80 threshold
  if (discountValue === 10) {
    const items = serverAutomaticDiscount?.automatic_discount_preview?.items;
    if (!items || items.length === 0) {
      return false;
    }

    // Calculate total cart value
    const totalVariantAmount = items.reduce(
      (sum, item) => sum + (item.variant_amount ?? 0),
      0,
    );

    // If cart total >= $80 but still no qualified products,
    // it means products are not eligible at all (disable BFCM)
    if (totalVariantAmount >= minimumThreshold) {
      return false;
    }

    // If cart total < $80, user is on path to qualification
    // (They just need to reach $80 threshold, so enable BFCM)
    return true;
  }

  // For any other discount value (< 10), disable BFCM
  return false;
}

/**
 * Applies BFCM discount to item price and savings
 * @param variantId - The Shopify variant ID (gid format)
 * @param itemPrice - The calculated display price
 * @param quantity - The quantity of items
 * @param bfcmData - BFCM discount data
 * @returns Object with updated DisplayPrice (if discount applied) and flag indicating if discount was applied
 */
export function applyBfcmDiscount(
  variantId: string,
  itemPrice: DisplayPrice,
  quantity: number,
  bfcmData?: BfcmData,
): BfcmDiscountResult {
  // Return no discount if BFCM is not enabled or no discount data
  if (!bfcmData?.isEnabled || !bfcmData.discount) {
    return { hasDiscount: false };
  }

  // Extract numeric ID from Shopify GID format
  const numericVariantId = extractIdFromShopifyStr(variantId);

  // Find matching discount item
  const discountItem = bfcmData.discount.items.find(
    (item) => item.external_id === numericVariantId && item.qualified === true,
  );

  // Return no discount if no matching qualified discount
  if (!discountItem) {
    return { hasDiscount: false };
  }

  // Convert cents to dollars with 2 decimal places
  const discountedSubtotal = (discountItem.discounted_amount / 100).toFixed(2);
  const savingsAmount = (discountItem.value / 100).toFixed(2);

  return {
    price: {
      ...itemPrice,
      subtotal: discountedSubtotal,
      savings: savingsAmount,
    },
    hasDiscount: true,
  };
}

/**
 * Calculates the additional amount for gifting products (subscription-gifting handle)
 * Adds the bundle price and savings to get the total gift value
 */
function calculateGiftingAmount(
  getProperty: (key: string) => string | undefined,
): number {
  const giftsStr = getProperty('gifts');
  if (!giftsStr) return 0;

  const gifts = parseRubyHashToJson<{
    bundle?: { price?: number; savings?: number };
  }>(giftsStr, 'gifts');

  if (!gifts?.bundle) return 0;

  const { price = 0, savings = 0 } = gifts.bundle;
  return Number(price) + Number(savings);
}

/**
 * Calculates the additional amount for PlayGenius subscription products
 * Uses either the bundle amount (for prepay-and-save) or price + savings (for regular subscriptions)
 */
function calculateSubscriptionAmount(
  getProperty: (key: string) => string | undefined,
): number {
  const subscriptionsPayloadStr = getProperty('subscriptionsPayload');
  if (!subscriptionsPayloadStr) return 0;

  const payload = parseRubyHashToJson<{
    bundle?: { amount?: number; price?: number; savings?: number };
    properties?: { item_origin?: string };
  }>(subscriptionsPayloadStr, 'subscriptionsPayload');

  if (!payload?.bundle) return 0;

  const { amount = 0, price = 0, savings = 0 } = payload.bundle;
  const itemOrigin = payload.properties?.item_origin;

  // Prepay-and-save uses the full bundle amount
  if (itemOrigin === ITEM_ORIGINS.PREPAY_AND_SAVE) {
    return Number(amount);
  }

  // Regular subscriptions add price and savings
  return Number(price) + Number(savings);
}

/**
 * Calculates the additional amount for book bundle products
 * Replaces the original price with the bundle price
 */
function calculateBookBundleAmount(
  getProperty: (key: string) => string | undefined,
  originalPrice: number,
): number {
  const priceStr = getProperty('price');
  if (!priceStr) return 0;

  const bookPrice = Number(priceStr);
  return bookPrice - originalPrice;
}

/**
 * Calculates the final amount for a cart item based on its product type
 * Applies special pricing logic for gifting, subscriptions, book bundles, and custom display prices
 */
function calculateItemAmount(
  originalPrice: number,
  productHandle: string,
  getProperty: (key: string) => string | undefined,
): number {
  // Priority order: gifting > subscriptions > book bundles > price_source + display_price > original price
  if (productHandle === 'subscription-gifting') {
    return originalPrice + calculateGiftingAmount(getProperty);
  }

  if (getProperty('is_playgenius_subscription') === 'true') {
    return originalPrice + calculateSubscriptionAmount(getProperty);
  }

  if (getProperty('is_book_bundle') === 'true') {
    return (
      originalPrice + calculateBookBundleAmount(getProperty, originalPrice)
    );
  }

  if (getProperty('price_source') && getProperty('display_price')) {
    return Number(getProperty('display_price'));
  }

  return originalPrice;
}

/**
 * Format cart items for the automatic discount preview API
 * Pure function that transforms cart line nodes into the format expected by the API
 * Replicates the logic from cart.liquid:84
 *
 * @param items - Array of cart line nodes from Shopify
 * @returns Formatted items for the automatic discount preview API, or undefined if empty
 */
export function formatCartItemsForDiscountPreview(
  items: CartLineNode[],
):
  | Array<{ external_id: string; quantity: number; amount: number }>
  | undefined {
  if (!items?.length) return undefined;

  return items.map((item) => {
    const { attributes, merchandise, quantity } = item;
    const productHandle = merchandise.product?.handle || '';
    const variantId = extractIdFromShopifyStr(merchandise.id);

    // Convert price from dollars to cents for the API
    const priceAmount = merchandise.price?.amount || '0';
    const originalPriceInCents = Math.round(
      parseFloat(String(priceAmount)) * 100,
    );

    // Create property getter that checks both regular and underscore-prefixed attribute keys
    const attributesMap = createAttributesMap(attributes || []);
    const getProperty = createGetPropertyFunction(attributesMap);

    // Calculate final amount based on product type (gifting, subscription, book bundle, or regular)
    const amount = calculateItemAmount(
      originalPriceInCents,
      productHandle,
      getProperty,
    );

    return {
      external_id: variantId,
      quantity,
      amount,
    };
  });
}
